import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useJoinPublicVideoRoom, useJoinVideoRoom } from "services/video";
import { VideoRoom } from "protogen/video_service_pb";
import { Note } from "protogen/notes_pb";
import { Suggestions } from "protogen/suggestions_pb";
import { FamilyStub } from "protogen/common_pb";
import { AccountStub } from "protogen/common_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdvisor } from "common/userUtils";
import { LocalUserChoices } from "@livekit/components-react";
import VideoCallAdvisor from "components/video/VideoCallAdvisor";
import VideoCallMember from "components/video/VideoCallMember";

const VideoCall: React.FC = () => {
  const currentUser = useContext(CurrentUserContext);
  const isAdvisor = isUserAdvisor(currentUser);
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [videoRoom, setVideoRoom] = useState<VideoRoom | null>(null);
  const [note, setNote] = useState<Note | null>(null);
  const [participants, setParticipants] = useState<AccountStub[]>([]);
  const [token, setToken] = useState<string>("");
  const [family, setFamily] = useState<FamilyStub | undefined>(undefined);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [callEnded, setCallEnded] = useState<boolean>(false);
  const [wasCreatingState, setWasCreatingState] = useState<boolean>(false);
  const [userChoices, setUserChoices] = useState<LocalUserChoices | null>(null);
  const joinVideoRequestFn = currentUser.ref
    ? useJoinVideoRoom
    : useJoinPublicVideoRoom;
  const { request: joinVideoRoom } = joinVideoRequestFn();

  const fetchAndJoinVideoRoom = useCallback(
    async (hostRejoin: boolean = false) => {
      if (!uuid) return;
      const response = await joinVideoRoom({ uuid, hostRejoin });
      const room = response?.videoRoom || null;

      setVideoRoom(room);
      setNote(response?.note || null);
      setParticipants(response?.participants || []);
      setToken(response?.token || "");
      setFamily(response?.family);
      setSuggestions(response?.suggestions || null);
      setCallEnded(false);

      // Track if we're transitioning from creating state
      if (room?.status === "creating") {
        setWasCreatingState(true);
      }
    },
    [uuid, joinVideoRoom, isAdvisor],
  );

  const handleCallEnd = () => {
    setCallEnded(true);
  };

  const handleReJoin = () => {
    fetchAndJoinVideoRoom(true);
  };

  const handleVideoSetupJoin = (choices: LocalUserChoices) => {
    // Store the user choices for when the call starts
    setUserChoices(choices);
    fetchAndJoinVideoRoom(true);
  };

  useEffect(() => {
    fetchAndJoinVideoRoom();
  }, []);

  if (!uuid) {
    navigate("/video");
    return null;
  }

  const isCallActive = videoRoom?.status === "active" && !callEnded;

  return isAdvisor ? (
    <VideoCallAdvisor
      videoRoom={videoRoom}
      participants={participants}
      handleCallEnd={handleCallEnd}
      note={note}
      token={token}
      family={family}
      isCallActive={isCallActive}
      onReJoin={handleReJoin}
      suggestions={suggestions}
      onVideoSetupJoin={handleVideoSetupJoin}
      wasCreatingState={wasCreatingState}
      userChoices={userChoices}
    />
  ) : (
    <VideoCallMember
      handleCallEnd={handleCallEnd}
      token={token}
      isCallActive={isCallActive}
      onReJoin={handleReJoin}
    />
  );
};

export default VideoCall;
